import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'sharedUi/Button';
import { notificationRunsService, type EventPublishResult } from '../../services/notification-runs.service';
import { notificationMastersService, type ParameterDefinition } from '../../services/notification-masters.service';
import { extractErrorMessage } from '../../utils/error-handler';

/** A value to start from, so the box is a form to fill rather than a blank page.
 *  The declared sample or default when the master states one — those are the
 *  author's own words for what belongs there — else an empty value of the right
 *  shape, which also documents the type. */
const seedFor = (p: ParameterDefinition): unknown => {
  if (p.defaultValue !== undefined && p.defaultValue !== null) return p.defaultValue;
  if (p.sample !== undefined && p.sample !== null) return p.sample;
  switch ((p.dataType || '').toLowerCase()) {
    case 'number': case 'integer': case 'decimal': return 0;
    case 'boolean': return false;
    case 'array': return [];
    case 'object': return {};
    default: return '';
  }
};

/**
 * Announce an EVENT schedule's own event, by hand.
 *
 * An EVENT schedule is never polled — it waits for its `eventKey` to be
 * published — so the only way to see whether it is wired to the key it claims is
 * to raise that key. Normally a producing system does this; here an operator can,
 * to check the wiring.
 *
 * **Check first, then send.** Raising the event really fires the notification to
 * its real recipients, so the dialog leads with a dry run: it reports which
 * schedules WOULD fire and messages nobody. Sending for real is the second,
 * deliberate press.
 *
 * **One key, possibly several listeners.** An event is published to the KEY, not
 * to the schedule being viewed: every schedule listening for it fires, and each
 * targets its own notification with its own declared parameters. So the payload
 * opens seeded from this schedule's notification, and the dry run — which names
 * every schedule that matched — extends it with whatever the others declare.
 * Seeding from one and sending to all is how a second listener renders gaps
 * nobody sees until it has already been delivered.
 */
const RaiseEventDialog = ({ eventKey, scheduleCode, masterId, parameters, onClose, onFired }: {
  eventKey: string;
  scheduleCode?: string;
  /** The notification this schedule fires — it declares what the payload holds. */
  masterId?: string;
  /** The schedule's own parameters, which an event's payload merges over. */
  parameters?: Record<string, unknown>;
  onClose: () => void;
  onFired: () => void;
}) => {
  const [payload, setPayload] = useState('');
  const [touched, setTouched] = useState(false);

  /* What the target notification says its templates need. The payload is exactly
     this: a fire whose payload omits a declared parameter renders a gap in the
     message, which is only visible once somebody has received it. */
  const masterQ = useQuery({
    queryKey: ['notification-master', masterId],
    queryFn: () => notificationMastersService.getById(masterId!),
    enabled: !!masterId,
  });
  const declared: ParameterDefinition[] = useMemo(
    () => masterQ.data?.parameters ?? [], [masterQ.data]);

  /* Pre-filled from the declaration, with the schedule's own parameters on top —
     the same precedence the engine applies, so what is shown is what would be
     sent. Never overwrites typing: once the box has been edited it is the
     operator's. */
  useEffect(() => {
    if (touched) return;
    const seeded: Record<string, unknown> = {};
    declared.forEach((p) => { seeded[p.name] = seedFor(p); });
    const merged = { ...seeded, ...(parameters || {}) };
    setPayload(JSON.stringify(merged, null, 2));
  }, [declared, parameters, touched]);
  const [busy, setBusy] = useState<'' | 'check' | 'send'>('');
  const [result, setResult] = useState<EventPublishResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Parameters added because another listener declared them. */
  const [alsoNeeded, setAlsoNeeded] = useState<string[]>([]);

  /** Add what the OTHER matched notifications declare, without touching values
   *  already entered — a key nobody typed is a gap in somebody's message. */
  const extendForOtherListeners = async (matched: EventPublishResult['schedules']) => {
    const others = [...new Set((matched || [])
      .map((m) => m.masterId).filter((id): id is string => !!id && id !== masterId))];
    if (!others.length) return;

    const masters = await Promise.all(others.map(async (id) => {
      try {
        return await notificationMastersService.getById(id);
      } catch {
        return null;  // a listener we cannot read is not a reason to block the check
      }
    }));

    let current: Record<string, unknown>;
    try {
      current = JSON.parse(payload || '{}');
    } catch {
      return;  // unparseable text belongs to the operator; leave it alone
    }
    const added: string[] = [];
    masters.forEach((m) => (m?.parameters ?? []).forEach((prm) => {
      if (!(prm.name in current)) {
        current[prm.name] = seedFor(prm);
        added.push(prm.name);
      }
    }));
    if (added.length) {
      setPayload(JSON.stringify(current, null, 2));
      setAlsoNeeded(added);
    }
  };

  const publish = async (dryRun: boolean) => {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(payload || '{}');
      if (typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error();
    } catch {
      setError('The payload must be a JSON object.');
      return;
    }
    setBusy(dryRun ? 'check' : 'send');
    setError(null);
    setResult(null);
    try {
      const res = await notificationRunsService.publishEvent(eventKey, parsed, undefined, dryRun);
      setResult(res);
      if (dryRun) await extendForOtherListeners(res.schedules);
      else onFired();
    } catch (e) {
      setError(extractErrorMessage(e, 'Could not publish the event'));
    } finally {
      setBusy('');
    }
  };

  return (
    <Dialog
      header={`Raise "${eventKey}"`}
      visible
      onHide={onClose}
      style={{ width: '32rem' }}
      data-testid="RaiseEvent.Dialog"
    >
      <p className="mb-3 text-xs text-ink-muted">
        Publishes this event to every schedule listening for
        {' '}<span className="font-mono">{eventKey}</span>
        {scheduleCode ? <> — including <span className="font-mono">{scheduleCode}</span></> : null}.
        Others may listen for the same key. Check first: it names every schedule that
        would fire, and sends nothing.
      </p>

      <label className="mb-1 block text-xs font-medium text-ink">Payload (JSON)</label>
      <InputTextarea
        value={payload}
        onChange={(e) => { setPayload(e.target.value); setTouched(true); setError(null); }}
        rows={8}
        className="w-full font-mono text-xs"
        data-testid="RaiseEvent.Payload"
      />
      <p className="mt-1 text-caption text-ink-muted">
        {masterQ.isLoading ? 'Reading what this notification declares…'
          : declared.length
            ? <>Filled from what this notification declares. Required:{' '}
              <span className="font-mono">
                {declared.filter((p) => p.isRequired !== false).map((p) => p.name).join(', ') || 'none'}
              </span>. A missing value renders as a gap in the message.</>
            : 'This notification declares no parameters, so an empty payload is complete.'}
      </p>

      {!!alsoNeeded.length && (
        <p className="mt-1 text-caption text-amber-700 dark:text-amber-400"
           data-testid="RaiseEvent.AlsoNeeded">
          Another schedule listening for this event needs{' '}
          <span className="font-mono">{alsoNeeded.join(', ')}</span> — added above, fill them in.
        </p>
      )}

      {error && (
        <div className="mt-3 rounded border border-red-300 bg-red-50 p-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200">
          {error}
        </div>
      )}

      {result && (
        <div
          className={`mt-3 rounded border p-2 text-sm ${result.dryRun
            ? 'border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-100'
            : 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-200'}`}
          data-testid="RaiseEvent.Result"
        >
          {result.dryRun
            ? `${result.matched ?? 0} schedule(s) would fire. Nothing was sent.`
            : `${result.dispatched ?? 0} of ${result.matched ?? 0} schedule(s) fired, `
              + `${result.notifications?.length ?? 0} notification(s) raised.`}
          {!!result.schedules?.length && (
            <div className="mt-1 font-mono text-xs">
              {result.schedules.map((s) => s.scheduleCode || s.scheduleId).join(', ')}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button label="Close" severity="secondary" text onClick={onClose} testId="RaiseEvent.Close" />
        <Button label="Check" icon="pi pi-search" severity="secondary" outlined
                loading={busy === 'check'} disabled={!!busy}
                onClick={() => publish(true)} testId="RaiseEvent.Check" />
        <Button label="Raise event" icon="pi pi-bolt"
                loading={busy === 'send'} disabled={!!busy}
                onClick={() => publish(false)} testId="RaiseEvent.Send" />
      </div>
    </Dialog>
  );
};

export default RaiseEventDialog;
